import { ExportWheelData, Wheel, WheelEntry } from '../types';

export function exportWheelToJSON(
  wheel: Wheel,
  entries: WheelEntry[]
): void {
  const exportData: ExportWheelData = {
    version: 1,
    exportedAt: Date.now(),
    name: wheel.name,
    description: wheel.description,
    colorThemeId: wheel.colorThemeId,
    spinDuration: wheel.spinDuration,
    removeWinnerAfterSpin: wheel.removeWinnerAfterSpin,
    entries: entries.map((e) => ({
      label: e.label,
      color: e.color,
      weight: e.weight || 1,
    })),
  };

  const jsonStr = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const safeFileName = wheel.name.replace(/[^a-z0-9_-]/gi, '_').toLowerCase() || 'wheel';
  const a = document.createElement('a');
  a.href = url;
  a.download = `chittiwin_${safeFileName}_${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ImportValidationResult {
  valid: boolean;
  error?: string;
  data?: {
    name: string;
    description?: string;
    colorThemeId?: string;
    spinDuration?: number;
    removeWinnerAfterSpin?: boolean;
    entries: Array<{ label: string; color?: string; weight?: number }>;
  };
}

export function validateAndParseWheelJSON(jsonText: string): ImportValidationResult {
  try {
    const raw = JSON.parse(jsonText);
    if (!raw || typeof raw !== 'object') {
      return { valid: false, error: 'File does not contain a valid JSON object.' };
    }

    if (!raw.name || typeof raw.name !== 'string' || raw.name.trim() === '') {
      return { valid: false, error: 'Wheel JSON is missing a valid "name" property.' };
    }

    if (!Array.isArray(raw.entries)) {
      return { valid: false, error: 'Wheel JSON is missing a valid "entries" list.' };
    }

    const cleanedEntries: Array<{ label: string; color?: string; weight?: number }> = [];
    for (const item of raw.entries) {
      if (typeof item === 'string' && item.trim() !== '') {
        cleanedEntries.push({ label: item.trim() });
      } else if (item && typeof item === 'object' && typeof item.label === 'string' && item.label.trim() !== '') {
        cleanedEntries.push({
          label: item.label.trim(),
          color: typeof item.color === 'string' ? item.color : undefined,
          weight: typeof item.weight === 'number' && item.weight > 0 ? item.weight : 1,
        });
      }
    }

    if (cleanedEntries.length === 0) {
      return { valid: false, error: 'No valid non-empty entries found in the file.' };
    }

    return {
      valid: true,
      data: {
        name: raw.name.trim(),
        description: typeof raw.description === 'string' ? raw.description : undefined,
        colorThemeId: typeof raw.colorThemeId === 'string' ? raw.colorThemeId : 'vibrant',
        spinDuration: typeof raw.spinDuration === 'number' ? raw.spinDuration : 5,
        removeWinnerAfterSpin: Boolean(raw.removeWinnerAfterSpin),
        entries: cleanedEntries,
      },
    };
  } catch (err: unknown) {
    return {
      valid: false,
      error: `Failed to parse JSON file: ${err instanceof Error ? err.message : 'Invalid JSON format'}`,
    };
  }
}
